/** @format */

import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { BsWrenchAdjustable } from "react-icons/bs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Cookies from "js-cookie";

const ViewAttendanceInOut = () => {
  const [selectedLeaveCat, setSelectedLeaveCat] = useState(null); // null = No Pay

  const [searchParams] = useSearchParams();
  const empId = searchParams.get("employeeId") || "";
  const year = searchParams.get("year") || "";
  const month = searchParams.get("month") || "";
  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const fmt = (t) => (t && t !== "00:00:00" ? t : "—");
  const rowKey = (idx) => `${rows[idx]?.["Date"] ?? "n/a"}#${idx}`;
  const [showAction, setShowAction] = useState(false);
  const [actionRow, setActionRow] = useState(null);
  // at the top with other state
  const [limit, setLimit] = useState(32); // default page size
  const [noAccessOpen, setNoAccessOpen] = useState(false);
  // Leave counts for the popup
  const [leaveCounts, setLeaveCounts] = useState({
    categories: [],
    noPay: null,
  });
  const [leaveCountsLoading, setLeaveCountsLoading] = useState(false);
  const [leaveCountsError, setLeaveCountsError] = useState("");
  const token = Cookies.get("user_token");

  // form state
  const [formType, setFormType] = useState("full day");
  const [isPosting, setIsPosting] = useState(false);
  const [postBanner, setPostBanner] = useState({ type: "", text: "" }); // success | error
  // Select/unselect one row
  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const openAction = (row) => {
    setActionRow(row);
    setFormType("full day");
    setSelectedLeaveCat(null); // default to No Pay
    setPostBanner({ type: "", text: "" });
    setShowAction(true);
  };

  const handleActionClick = (row) => {
    const isCompleteYes = String(row?.complete ?? "").toLowerCase() === "yes";
    if (isCompleteYes) {
      setNoAccessOpen(true);
    } else {
      openAction(row);
    }
  };

  const canSubmitAttendance = useMemo(() => {
    if (isLoading || fetchError) return false;
    if (!Array.isArray(rows) || rows.length === 0) return false;
    return rows.every(
      (r) =>
        String(r?.complete ?? "")
          .trim()
          .toLowerCase() === "yes"
    );
  }, [rows, isLoading, fetchError]);

  const reasonForCategory = (name) => {
    const s = String(name || "").toUpperCase();
    if (s.includes("ANNUAL") || s.includes("ANUAL")) return "Annual leave";
    if (s.includes("HALF")) return "Half day";
    return name || "Leave";
  };

  useEffect(() => {
    setSelected(new Set());
  }, [rows]);

  useEffect(() => {
    if (selectedLeaveCat?.name && /half/i.test(selectedLeaveCat.name)) {
      setFormType("half day");
    }
  }, [selectedLeaveCat]);

  const closeAction = () => {
    setShowAction(false);
    setActionRow(null);
    setPostBanner({ type: "", text: "" });
  };
  const fetchAttendance = async (signal) => {
    setIsLoading(true);
    setFetchError("");
    try {
      const params = new URLSearchParams({
        employeeId: empId,
        year: String(year),
        month: String(Number(month)), // avoid leading zero
        page: String(page),
        limit: String(limit),
      });

      const res = await fetch(
        `${API_URL}/v1/hris/leave-check/real-leaves?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          signal,
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setEmployeeMeta(json?.employee ?? null);

      const data = Array.isArray(json?.data) ? json.data : [];
      setRows(data);

      // pagination from backend
      const pag = json?.pagination || {};
      const total = Number(pag.total);
      const totalPages = Number(pag.totalPages);
      const srvPage = Number(pag.page);
      const srvLimit = Number(pag.limit);

      setCount(Number.isFinite(total) ? total : data.length);
      setTotalPages(Number.isFinite(totalPages) ? totalPages : 1);

      if (Number.isFinite(srvPage) && srvPage !== page) setPage(srvPage);
      if (Number.isFinite(srvLimit) && srvLimit !== limit) setLimit(srvLimit);

      if (Number.isFinite(totalPages) && totalPages > 0 && page > totalPages) {
        setPage(1);
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        setFetchError("Failed to load attendance.");
        setRows([]);
        setCount(0);
        setTotalPages(1);
      }
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (!empId || !year || !month) return;
    const controller = new AbortController();
    fetchAttendance(controller.signal);
    return () => controller.abort();
  }, [empId, year, month, page, limit, API_URL]);

  const openConfirm = () => {
    if (selected.size === 0) return;
    setShowConfirm(true);
  };

  const statusBadge = (status) => {
    if (status == null) return "—";
    const text = typeof status === "string" ? status : String(status);
    const normalized = text.toLowerCase();

    let cls = "bg-gray-200 text-gray-700"; // default
    if (normalized.includes("leave")) {
      cls = "bg-red-100 text-red-700"; // Leave → red
    } else if (
      normalized.includes("short") ||
      normalized.includes("normal day")
    ) {
      cls = "bg-yellow-100 text-yellow-700"; // Short in / Normal day → yellow
    } else if (normalized.includes("half")) {
      cls = "bg-orange-100 text-orange-700"; // Half day → orange
    } else if (normalized === "normal day") {
      cls = "bg-gray-300 text-gray-800"; // strictly "Normal day" → gray
    }

    return (
      <span
        className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${cls}`}
      >
        {text}
      </span>
    );
  };
  const leaveBadge = (leaves) => {
    if (!Array.isArray(leaves) || leaves.length === 0) return "—";

    // Find the first approved/rejected/pending leave (if any)
    const leave = leaves[0];
    const category = leave.category || "—";
    const status = (leave.status || "").toUpperCase();

    let cls = "bg-gray-200 text-gray-700";
    if (status === "APPROVED") cls = "bg-green-100 text-green-700";
    else if (status === "REJECTED") cls = "bg-red-100 text-red-700";
    else if (status === "PENDING") cls = "bg-yellow-100 text-yellow-700";

    return (
      <span
        className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${cls}`}
      >
        {`${category.trim()} (${status || "—"})`}
      </span>
    );
  };

  const submitNoPay = async () => {
    const requestDay = actionRow?.["Date"];
    if (!requestDay) {
      // optional toast for validation error
      toast.error("Date missing for this row.");
      return;
    }

    const normalizedType = String(formType || "")
      .toLowerCase()
      .includes("half")
      ? "half day"
      : "full day";

    // Build payload based on selection (category vs no-pay)
    const payload = selectedLeaveCat?.id
      ? {
        employee_no: empId,
        leave_category_id: Number(selectedLeaveCat.id),
        reason: reasonForCategory(selectedLeaveCat.name),
        requested_dates: [{ request_day: requestDay, type: normalizedType }],
      }
      : {
        employee_no: empId,
        reason: "No-pay leave added by HR",
        requested_dates: [{ request_day: requestDay, type: normalizedType }],
      };

    setIsPosting(true);
    setPostBanner({ type: "", text: "" }); // optional: clear inline banner

    try {
      const res = await fetch(`${API_URL}/v1/hris/leave-check/no-pay-by-hr`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);

      //  Toast success with message from API
      toast.success(json?.message || "Saved successfully.");

      // Refresh table + leave balances
      try {
        if (typeof fetchAttendance === "function") await fetchAttendance();
        if (typeof fetchLeaveCounts === "function") await fetchLeaveCounts();
      } catch { }

      closeAction();
    } catch (err) {
      //  Toast error
      toast.error(err?.message || "Failed to save.");
    } finally {
      setIsPosting(false);
    }
  };

  const [employeeMeta, setEmployeeMeta] = useState(null);

  const initials = (s = "") =>
    s
      .trim()
      .split(/\s+/)
      .map((w) => w[0] || "")
      .join("")
      .slice(0, 2)
      .toUpperCase() || "NA";

  const fetchLeaveCounts = async (signal) => {
    if (!empId || !year) return;
    setLeaveCountsLoading(true);
    setLeaveCountsError("");
    try {
      const params = new URLSearchParams({
        employee_no: empId, // this endpoint expects employee_no
        year: String(year),
      });
      const res = await fetch(
        `${API_URL}/v1/hris/leave-check/employee-leave-counts?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          signal,
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const cats = json?.data?.categories ?? [];
      const noPay = json?.data?.no_pay ?? null;
      setLeaveCounts({ categories: cats, noPay });
    } catch (e) {
      if (e.name !== "AbortError") {
        setLeaveCounts({ categories: [], noPay: null });
        setLeaveCountsError("Failed to load leave counts.");
      }
    } finally {
      setLeaveCountsLoading(false);
    }
  };
  useEffect(() => {
    if (!showAction) return;
    const controller = new AbortController();
    fetchLeaveCounts(controller.signal);
    return () => controller.abort();
  }, [showAction, empId, year, API_URL]);

  const submitAttendance = async () => {
    try {
      const res = await fetch(
        `${API_URL}/v1/hris/payroll/attendance-status/update`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            employee_id: empId,
            month: Number(month),
            year: Number(year),
          }),
        }
      );
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(json?.message || "Attendance submitted.");
        setShowConfirm(false);
        // refresh the table if you want
        const controller = new AbortController();
        await fetchAttendance(controller.signal);
      } else {
        toast.error(json?.message || "Failed to submit attendance.");
      }
    } catch {
      toast.error("Network error. Please try again.");
    }
  };

  return (
    <div className="mx-5 mt-5 font-montserrat">
      <div className="flex items-center justify-between mb-5">
        <p className="text-[24px]">
          Attendance CheckIn / Out Report for · {empId} · {year}-
          {String(month).padStart(2, "0")}
        </p>
      </div>

      <div className="shadow-lg p-5 rounded-lg bg-white">
        {/* Actions */}
        <div className="flex justify-between items-center mb-3">
          <button
            className={`px-4 py-2 rounded text-white ${canSubmitAttendance
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-blue-300 cursor-not-allowed"
              }`}
            disabled={!canSubmitAttendance}
            onClick={() => setShowConfirm(true)}
            title={
              canSubmitAttendance
                ? "Submit Attendance"
                : "All days must be complete to submit"
            }
          >
            Submit Attendance
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 font-medium text-gray-900">Date</th>
                <th className="px-6 py-3 font-medium text-gray-900">
                  Check-in
                </th>
                <th className="px-6 py-3 font-medium text-gray-900">IN Type</th>
                <th className="px-6 py-3 font-medium text-gray-900">
                  Check-out
                </th>
                <th className="px-6 py-3 font-medium text-gray-900">
                  OUT Type
                </th>
                <th className="px-6 py-3 font-medium text-gray-900">Status</th>
                <th className="px-6 py-3 font-medium text-gray-900">Leave</th>
                <th className="px-6 py-3 font-medium text-gray-900">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center">
                    Loading...
                  </td>
                </tr>
              ) : fetchError ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-4 text-center text-red-600"
                  >
                    {fetchError}
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((r, idx) => {
                  const id = rowKey(idx);
                  const checked = selected.has(id);
                  return (
                    <tr key={id} className="hover:bg-gray-50">
                      <td className="px-6 py-2">{r["Date"]}</td>
                      <td className="px-6 py-2">{fmt(r["Checkin-time"])}</td>
                      <td className="px-6 py-2">{r["Checkin-type"] || "—"}</td>
                      <td className="px-6 py-2">{fmt(r["Checkout-time"])}</td>
                      <td className="px-6 py-2">{r["Checkout-type"] || "—"}</td>
                      <td className="px-6 py-2">{statusBadge(r["Status"])}</td>
                      <td className="px-6 py-2">{leaveBadge(r["Leaves"])}</td>
                      <td className="px-6 py-2">
                        {(() => {
                          const isCompleteYes =
                            String(r?.complete ?? "").toLowerCase() === "yes";

                          const hasApprovedLeave = Array.isArray(r.Leaves)
                            ? r.Leaves.some(
                              (l) =>
                                String(l.status || "").toUpperCase() ===
                                "APPROVED"
                            )
                            : false;

                          // ✅ Disable if Complete, Approved Leave, or Status === "Edited"
                          const isEditedStatus =
                            String(r?.Status ?? "").toLowerCase() === "edited";

                          const isDisabled =
                            isCompleteYes || hasApprovedLeave || isEditedStatus;

                          return (
                            <button
                              type="button"
                              onClick={() =>
                                !isDisabled && handleActionClick(r)
                              }
                              className={`p-2 rounded ${isDisabled
                                ? "text-gray-300 cursor-not-allowed"
                                : "hover:bg-gray-100 text-gray-600"
                                }`}
                              aria-disabled={isDisabled}
                              title={
                                hasApprovedLeave
                                  ? "Leave already approved"
                                  : isCompleteYes
                                    ? "No access"
                                    : isEditedStatus
                                      ? "Already edited"
                                      : "Adjust"
                              }
                            >
                              <BsWrenchAdjustable />
                            </button>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center">
                    No data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pager */}
        <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
          <div>
            Page {Math.min(page, totalPages)} of {totalPages} ({count} records)
          </div>
          <div className="flex gap-2">
            <button
              className="px-3 py-1 border rounded disabled:opacity-50"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isLoading}
            >
              Prev
            </button>
            <button
              className="px-3 py-1 border rounded disabled:opacity-50"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || isLoading}
            >
              Next
            </button>
          </div>
        </div>
      </div>
      {showAction && actionRow && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md relative">
            {/* Close */}
            <button
              onClick={closeAction}
              className="absolute top-2 right-2 text-gray-500 text-xl"
              disabled={isPosting}
              aria-label="Close"
            >
              &times;
            </button>

            {/* Header */}
            <div className="flex justify-center mb-4">
              <h2 className="text-xl font-bold">Assign No-Pay</h2>
            </div>

            {/* Employee Info */}
            <div className="flex items-center mb-2">
              <div className="h-10 w-10 flex-shrink-0 rounded-full bg-blue-300 flex items-center justify-center text-white font-bold">
                {initials(employeeMeta?.name || empId)}
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-900">
                  {employeeMeta?.name || empId}
                </div>
                {employeeMeta?.email && (
                  <div className="text-sm text-gray-500">
                    {employeeMeta.email}
                  </div>
                )}
              </div>
            </div>

            {/* Selected Day pill (like "Existing Components") */}
            <p className="mb-2 flex items-center">
              Selected Day:&nbsp;
              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                {actionRow["Date"]}
              </span>
            </p>
            {/* Leave Type (single-select chips) */}
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Leave Type
              </label>

              {leaveCountsLoading ? (
                <div className="text-sm text-gray-500">Loading…</div>
              ) : leaveCountsError ? (
                <div className="text-sm text-red-600">{leaveCountsError}</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {/* No Pay chip */}
                  <button
                    type="button"
                    onClick={() => setSelectedLeaveCat(null)}
                    className={`px-3 py-1 rounded-full text-xs border
          ${selectedLeaveCat === null
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                      }`}
                    disabled={isPosting}
                  >
                    No Pay
                  </button>

                  {/* Category chips */}
                  {leaveCounts.categories.map((c) => {
                    const isSel = selectedLeaveCat?.id === c.leave_category_id;
                    return (
                      <button
                        key={c.leave_category_id}
                        type="button"
                        onClick={() =>
                          setSelectedLeaveCat({
                            id: Number(c.leave_category_id), // 👈 keep the numeric ID
                            name: c.category_name,
                          })
                        }
                        className={`px-3 py-1 rounded-full text-xs border ${isSel
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                          }`}
                        disabled={isPosting}
                        title={`${c.category_name}: ${c.leave_count}`}
                      >
                        {c.category_name} ·{" "}
                        <span className="font-semibold">{c.leave_count}</span>
                      </button>
                    );
                  })}

                  {/* Optional No-pay summary chip */}
                  {leaveCounts.noPay && (
                    <span className="px-3 py-1 rounded-full text-xs bg-red-50 text-red-700 border border-red-200">
                      No-pay so far: {leaveCounts.noPay.days} day(s)
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Reason (read-only) — styled like "Component Name" */}
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100 text-gray-700"
                value="No-pay leave added by HR"
                readOnly
              />
            </div>

            {/* Two-column area (mirrors Amount/Rate layout) */}
            <div className="flex gap-4 mb-4">
              {/* Employee No (read-only) */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee No
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    className="border border-gray-300 rounded-l px-3 py-2 w-full bg-gray-100 text-gray-700"
                    value={empId}
                    readOnly
                  />
                  <span className="border border-gray-300 rounded-r px-3 py-2 bg-gray-50">
                    ID
                  </span>
                </div>
              </div>

              {/* Type (dropdown) */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <div className="flex items-center">
                  <select
                    className="border border-gray-300 rounded px-3 py-2 w-full bg-white"
                    value={formType}
                    onChange={(e) => setFormType(e.target.value)}
                    disabled={isPosting}
                  >
                    <option value="short_leave">Short Leave</option>
                    <option value="full day">Full day</option>
                    <option value="half day">Half day</option>
                  </select>
                </div>
              </div>
            </div>

            <p className="text-gray-500 text-sm mb-4">
              After you assign, a no-pay leave record will be added to this
              employee for the selected day.
            </p>

            {/* Inline banner (success/error) */}
            {postBanner.text && (
              <div
                className={`mb-4 px-3 py-2 rounded text-sm ${postBanner.type === "success"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-700"
                  }`}
              >
                {postBanner.text}
              </div>
            )}

            {/* Buttons */}
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                onClick={closeAction}
                disabled={isPosting}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 ${isPosting ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                onClick={submitNoPay}
                disabled={isPosting}
              >
                {isPosting ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {noAccessOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl p-5 shadow-xl w-full max-w-sm">
            <h4 className="text-lg font-semibold text-center text-gray-800">
              Action Locked
            </h4>
            <p className="text-center text-gray-700 mt-2">
              You don’t have access to perform this.
            </p>
            <div className="mt-4 flex justify-center">
              <button
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={() => setNoAccessOpen(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl p-5 shadow-xl w-full max-w-md">
            <h3 className="text-xl font-semibold text-center mb-2">
              Submit Attendance?
            </h3>
            <p className="text-center text-gray-600">
              {empId} · {year}-{String(month).padStart(2, "0")}
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
                onClick={() => setShowConfirm(false)}
              >
                No
              </button>
              <button
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={submitAttendance}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </div>
  );
};

export default ViewAttendanceInOut;
